import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ShieldOff, CheckCircle, ListFilter, Loader2, AlertCircle, RefreshCw, LayoutGrid, List, Calendar, BarChart3 } from 'lucide-react';
import api from '../api/axios';

const HISTORY_CACHE_KEY = 'Yassu:history_cache';

const ProdVitalsHistoryPage = () => {
  const [records, setRecords] = useState(() => {
    const saved = localStorage.getItem(HISTORY_CACHE_KEY);
    return saved ? JSON.parse(saved) : [];
  });
  const [displayRecords, setDisplayRecords] = useState([]);
  const [startDate, setStartDate] = useState(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [filterIp, setFilterIp] = useState('');
  const [filterServer, setFilterServer] = useState('ALL');
  const [filterStatus, setFilterStatus] = useState('ALL'); 
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState('matrix'); 

  const uniqueServers = useMemo(() => {
    return [...new Set(records.map(rec => rec.serverName))].sort();
  }, [records]);

  const fetchDailyStatus = useCallback(async (customStart, customEnd) => {
    setIsLoading(true);
    try {
      const start = customStart || startDate;
      const end = customEnd || endDate;
      const response = await api.get('/monitoring/daily-status', {
        params: {
          start,
          end
        }
      });
      const data = response.data;
      setRecords(data || []);
      localStorage.setItem(HISTORY_CACHE_KEY, JSON.stringify(data || []));
    } catch (err) {
      console.error("Fetch Error:", err);
      alert("Failed to fetch history from database. Check console for details.");
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate]);

  const setQuickRange = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    
    const startStr = start.toISOString().split('T')[0];
    const endStr = end.toISOString().split('T')[0];
    
    setEndDate(endStr);
    setStartDate(startStr);
    
    // Trigger immediate fetch with new dates
    fetchDailyStatus(startStr, endStr);
  };

  const clearFilters = () => {
    setFilterIp('');
    setFilterServer('ALL');
    setFilterStatus('ALL');
  };

  useEffect(() => {
    let filtered = [...records];

    if (filterIp) {
      filtered = filtered.filter(rec =>
        rec.ipAddress.includes(filterIp) || rec.serverName.toLowerCase().includes(filterIp.toLowerCase())
      );
    }

    if (filterServer !== 'ALL') {
      filtered = filtered.filter(rec => rec.serverName === filterServer);
    }

    if (filterStatus === 'HAVE_INBOX') {
        const successfulServers = new Set(filtered.filter(r => r.status === 'INBOX').map(r => r.serverName));
        const successfulIps = new Set(filtered.filter(r => r.status === 'INBOX').map(r => r.ipAddress));
        filtered = filtered.filter(rec => 
            successfulServers.has(rec.serverName) || successfulIps.has(rec.ipAddress)
        );
    } else if (filterStatus !== 'ALL') {
        filtered = filtered.filter(rec => rec.status === filterStatus);
    }

    // Sort from OLD to NEW (ascending)
    filtered.sort((a, b) => new Date(a.checkTime) - new Date(b.checkTime));
    setDisplayRecords(filtered);
  }, [filterIp, filterServer, filterStatus, records]);

  const uniqueDates = useMemo(() => {
    return [...new Set(displayRecords.map(rec => {
        const d = new Date(rec.checkTime);
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });
    }))].sort((a, b) => {
        const [da, ma] = a.split('/').map(Number);
        const [db, mb] = b.split('/').map(Number);
        // Sort from OLD to NEW (ascending)
        return new Date(2026, ma - 1, da) - new Date(2026, mb - 1, db);
    });
  }, [displayRecords]);

  const matrixData = useMemo(() => {
    const ipMap = {};
    displayRecords.forEach(rec => {
      if (!ipMap[rec.ipAddress]) {
        ipMap[rec.ipAddress] = {
          ipAddress: rec.ipAddress,
          serverName: rec.serverName,
          statusesByDay: {}
        };
      }
      const dateKey = new Date(rec.checkTime).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });
      const currentStatus = ipMap[rec.ipAddress].statusesByDay[dateKey];
      const newStatus = rec.status;

      if (!currentStatus) {
        ipMap[rec.ipAddress].statusesByDay[dateKey] = newStatus;
      } else {
        if (newStatus === 'INBOX' || (newStatus === 'SPAM' && currentStatus === 'NOT_DELIVERED')) {
            ipMap[rec.ipAddress].statusesByDay[dateKey] = newStatus;
        }
      }
    });

    return {
      dates: uniqueDates,
      rows: Object.values(ipMap).sort((a, b) => a.serverName.localeCompare(b.serverName))
    };
  }, [displayRecords, uniqueDates]);

  const serverDayMatrix = useMemo(() => {
    const serverMap = {};
    displayRecords.forEach(rec => {
        const name = rec.serverName || 'Unknown';
        if (!serverMap[name]) {
            serverMap[name] = { name, days: {} };
        }
        const dateKey = new Date(rec.checkTime).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });
        if (!serverMap[name].days[dateKey]) {
            serverMap[name].days[dateKey] = { inbox: 0, spam: 0 };
        }
        if (rec.status === 'INBOX') serverMap[name].days[dateKey].inbox++;
        if (rec.status === 'SPAM') serverMap[name].days[dateKey].spam++;
    });

    return {
        dates: uniqueDates,
        rows: Object.values(serverMap).sort((a, b) => a.name.localeCompare(b.name))
    };
  }, [displayRecords, uniqueDates]);

  const serverSummaryData = useMemo(() => {
    const serverMap = {};
    displayRecords.forEach(rec => {
        const name = rec.serverName || 'Unknown Server';
        if (!serverMap[name]) {
            serverMap[name] = { name, inbox: 0, spam: 0, notDelivered: 0, total: 0 };
        }
        serverMap[name].total++;
        if (rec.status === 'INBOX') serverMap[name].inbox++;
        else if (rec.status === 'SPAM') serverMap[name].spam++;
        else serverMap[name].notDelivered++;
    });

    return Object.values(serverMap).map(srv => ({
        ...srv,
        rate: srv.inbox + srv.spam > 0 ? Math.round((srv.inbox / (srv.inbox + srv.spam)) * 100) : 0
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, [displayRecords]);

  const getStatusStyle = (status) => {
    switch (status) {
      case 'INBOX': return { bg: 'bg-success', light: '#eafaf1', text: '#1e8449', icon: <CheckCircle size={14} /> };
      case 'SPAM': return { bg: 'bg-danger', light: '#fdedec', text: '#a93226', icon: <ShieldOff size={14} /> };
      case 'NOT_DELIVERED': return { bg: 'bg-warning', light: '#fef9e7', text: '#9a7d0a', icon: <AlertCircle size={14} /> };
      default: return { bg: 'bg-secondary', light: '#f4f6f6', text: '#566573', icon: null };
    }
  };

  return (
    <div className="container-fluid p-4 bg-light min-vh-100">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div className="d-flex align-items-center gap-3">
          <div className="bg-primary bg-opacity-10 p-3 rounded-3">
            <LayoutGrid size={24} className="text-primary" />
          </div>
          <div>
            <h4 className="fw-bold m-0">Infrastructure Monitor</h4>
            <p className="text-muted small m-0">Dynamic delivery status matrix</p>
          </div>
        </div>
        <div className="d-flex gap-2">
          <div className="btn-group shadow-sm bg-white">
            <button className={`btn ${viewMode === 'matrix' ? 'btn-primary' : 'btn-white'}`} onClick={() => setViewMode('matrix')} title="IP Matrix"><LayoutGrid size={18} /></button>
            <button className={`btn ${viewMode === 'serverMatrix' ? 'btn-primary' : 'btn-white'}`} onClick={() => setViewMode('serverMatrix')} title="Server Matrix"><Calendar size={18} /></button>
            <button className={`btn ${viewMode === 'summary' ? 'btn-primary' : 'btn-white'}`} onClick={() => setViewMode('summary')} title="Summary"><BarChart3 size={18} /></button>
            <button className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-white'}`} onClick={() => setViewMode('list')} title="Logs"><List size={18} /></button>
          </div>
          <button className="btn btn-white border d-flex align-items-center gap-2 shadow-sm" onClick={() => fetchDailyStatus()} disabled={isLoading}>
            {isLoading ? <Loader2 size={18} className="spin-animation" /> : <RefreshCw size={18} />}
            <span className="d-none d-md-inline">Refresh</span>
          </button>
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body p-4">
          <div className="row g-3">
            <div className="col-md-2">
              <label className="form-label small fw-bold text-uppercase text-muted">From</label>
              <input type="date" className="form-control border-0 bg-light shadow-none" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="col-md-2">
              <label className="form-label small fw-bold text-uppercase text-muted">To</label>
              <input type="date" className="form-control border-0 bg-light shadow-none" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
            <div className="col-md-2">
              <label className="form-label small fw-bold text-uppercase text-muted">Server Cluster</label>
              <select className="form-select border-0 bg-light shadow-none fw-bold text-dark" value={filterServer} onChange={(e) => setFilterServer(e.target.value)}>
                <option value="ALL">All Clusters</option>
                {uniqueServers.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small fw-bold text-uppercase text-muted">Status</label>
              <select className="form-select border-0 bg-light shadow-none fw-bold text-primary" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="ALL">All Records</option>
                <option value="HAVE_INBOX">Has Inbox Success</option>
                <option value="INBOX">Inbox Only</option>
                <option value="SPAM">Spam Only</option>
                <option value="NOT_DELIVERED">Not Delivered</option>
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label small fw-bold text-uppercase text-muted">Search IP</label>
              <div className="input-group">
                <span className="input-group-text border-0 bg-light px-2"><ListFilter size={18} className="text-muted" /></span>
                <input type="text" className="form-control border-0 bg-light shadow-none" placeholder="Search IP address..." value={filterIp} onChange={(e) => setFilterIp(e.target.value)} />
                <button className="btn btn-light border-0 px-2" onClick={clearFilters} title="Clear Filters">Clear</button>
              </div>
            </div>
            <div className="col-12 mt-3 d-flex flex-wrap gap-2 border-top pt-3 align-items-center">
              <span className="small fw-bold text-muted text-uppercase me-2">Quick Range:</span>
              <button className="btn btn-sm btn-light border rounded-pill px-3" onClick={() => setQuickRange(0)}>Today</button>
              <button className="btn btn-sm btn-light border rounded-pill px-3" onClick={() => setQuickRange(7)}>7 Days</button>
              <button className="btn btn-sm btn-light border rounded-pill px-3" onClick={() => setQuickRange(30)}>Month</button>
              <button className="btn btn-sm btn-light border rounded-pill px-3" onClick={() => setQuickRange(365)}>Year</button>
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm overflow-hidden">
        {viewMode === 'matrix' && (
          <div className="table-responsive">
            <table className="table table-bordered align-middle mb-0 text-center">
              <thead className="bg-dark text-white">
                <tr>
                  <th className="px-3 py-3 text-uppercase small text-start" style={{ minWidth: '220px' }}>IP / Server</th>
                  {uniqueDates.map(date => (
                    <th key={date} className="px-2 py-3 small">{date}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrixData.rows.length > 0 ? (
                  matrixData.rows.map(row => (
                    <tr key={row.ipAddress}>
                      <td className="px-3 py-2 text-start bg-white">
                        <div className="text-primary fw-black mb-0" style={{ fontSize: '18px', letterSpacing: '-0.5px' }}>{row.ipAddress}</div>
                        <div className="text-muted small fw-bold text-uppercase" style={{ fontSize: '9px' }}>{row.serverName}</div>
                      </td>
                      {uniqueDates.map(date => {
                        const status = row.statusesByDay[date];
                        const style = getStatusStyle(status);
                        return (
                          <td key={date} className="p-0 border-light">
                            {status ? (
                              <div className="h-100 w-100 py-2 d-flex flex-column align-items-center justify-content-center gap-0 shadow-inner" style={{ minHeight: '55px', backgroundColor: style.light, color: style.text }}>
                                <div className="fw-black text-uppercase" style={{ fontSize: '10px' }}>{status}</div>
                                <div style={{ opacity: 0.6 }}>{style.icon}</div>
                              </div>
                            ) : <div className="py-2 opacity-10 small fw-bold text-muted text-center">—</div>}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                ) : <tr><td colSpan={uniqueDates.length + 1} className="py-5 text-muted text-center">No data found.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'serverMatrix' && (
          <div className="table-responsive">
            <table className="table table-bordered align-middle mb-0 text-center">
              <thead className="bg-dark text-white">
                <tr>
                  <th className="px-3 py-3 text-uppercase small text-start" style={{ minWidth: '250px' }}>Server Cluster</th>
                  {uniqueDates.map(date => (
                    <th key={date} className="px-2 py-3 small">{date}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {serverDayMatrix.rows.length > 0 ? (
                  serverDayMatrix.rows.map(row => (
                    <tr key={row.name}>
                      <td className="px-3 py-2 text-start bg-white">
                        <div className="text-dark fw-black mb-0" style={{ fontSize: '18px', letterSpacing: '-0.5px' }}>{row.name}</div>
                        <span className="text-muted text-uppercase fw-bold" style={{ fontSize: '8px' }}>Health</span>
                      </td>
                      {uniqueDates.map(date => {
                        const dayData = row.days[date];
                        const total = (dayData?.inbox || 0) + (dayData?.spam || 0);
                        const rate = total > 0 ? Math.round((dayData.inbox / total) * 100) : 0;
                        const cellBg = rate > 70 ? '#eafaf1' : rate > 30 ? '#fef9e7' : '#fdedec';
                        const cellText = rate > 70 ? '#1e8449' : rate > 30 ? '#9a7d0a' : '#a93226';
                        return (
                          <td key={date} className="p-0 border-light">
                            {dayData ? (
                              <div className="h-100 w-100 py-2 d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '65px', backgroundColor: cellBg, color: cellText }}>
                                <div className="d-flex align-items-baseline gap-1">
                                  <span className="h5 m-0 fw-black">{dayData.inbox}</span>
                                  <span className="small opacity-50">/</span>
                                  <span className="small m-0 fw-bold">{dayData.spam}</span>
                                </div>
                                <div className="fw-black border-top border-dark border-opacity-10 mt-1 pt-1" style={{ fontSize: '10px' }}>{rate}%</div>
                              </div>
                            ) : <div className="py-2 opacity-10 small fw-bold text-muted text-center">—</div>}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                ) : <tr><td colSpan={uniqueDates.length + 1} className="py-5 text-muted text-center">No data found.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'summary' && (
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="bg-dark text-white">
                <tr><th className="px-3 py-3 text-uppercase small text-start">Server</th><th className="px-3 py-3 text-uppercase small text-center text-success">Inbox</th><th className="px-3 py-3 text-uppercase small text-center text-danger">Spam</th><th className="px-3 py-3 text-uppercase small text-center text-warning">Def.</th><th className="px-3 py-3 text-uppercase small text-center">Total</th><th className="px-3 py-3 text-uppercase small text-center">Rate</th></tr>
              </thead>
              <tbody>
                {serverSummaryData.length > 0 ? (
                  serverSummaryData.map((srv, idx) => (
                    <tr key={idx}>
                      <td className="px-3 py-2 text-start"><div className="text-dark fw-black" style={{ fontSize: '20px', letterSpacing: '-0.5px' }}>{srv.name}</div></td>
                      <td className="px-3 py-2 text-center text-success"><div className="h4 fw-bold m-0">{srv.inbox}</div></td>
                      <td className="px-3 py-2 text-center text-danger"><div className="h4 fw-bold m-0">{srv.spam}</div></td>
                      <td className="px-3 py-2 text-center text-warning"><div className="h4 fw-bold m-0">{srv.notDelivered}</div></td>
                      <td className="px-3 py-2 text-center text-muted"><div className="h5 fw-bold m-0">{srv.total}</div></td>
                      <td className="px-3 py-2 text-center"><div className={`h3 fw-black m-0 ${srv.rate > 70 ? 'text-success' : srv.rate > 30 ? 'text-warning' : 'text-danger'}`}>{srv.rate}%</div></td>
                    </tr>
                  ))
                ) : <tr><td colSpan="6" className="py-5 text-muted text-center">No servers found.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'list' && (
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="bg-light">
                <tr><th className="px-3 py-2 text-uppercase small">Timestamp</th><th className="px-3 py-2 text-uppercase small">Identity</th><th className="px-3 py-2 text-uppercase small text-center">Status</th></tr>
              </thead>
              <tbody>
                {displayRecords.map((rec) => {
                  const style = getStatusStyle(rec.status);
                  const d = new Date(rec.checkTime);
                  return (
                    <tr key={rec.id}>
                      <td className="px-3 py-1 small"><div className="fw-bold">{d.toLocaleDateString()}</div><div className="text-muted extra-small" style={{fontSize: '9px'}}>{d.toLocaleTimeString()}</div></td>
                      <td className="px-3 py-1"><div className="fw-bold text-primary" style={{ fontSize: '15px' }}>{rec.ipAddress}</div><div className="text-muted extra-small text-uppercase" style={{ fontSize: '9px' }}>{rec.serverName}</div></td>
                      <td className="px-3 py-1 text-center"><span className="badge rounded-pill px-2 py-1 fw-bold" style={{ backgroundColor: style.light, color: style.text, border: `1px solid ${style.text}20`, fontSize: '10px' }}>{rec.status}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProdVitalsHistoryPage;
